import type { OllamaHealthStatus } from "../types";

import { core } from "@tauri-apps/api";
import { useState, useEffect, useCallback } from "react";

import { debugLog } from "@/domains/logging";

/**
 * Custom hook for Ollama model health monitoring
 * Checks connection status and available models
 *
 * @returns Object with health status, available models, and health check functions
 *
 */
export const useModelHealth = () => {
  const [healthStatus, setHealthStatus] = useState<OllamaHealthStatus>({
    isHealthy: false,
    lastChecked: 0,
  });
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [isChecking, setIsChecking] = useState(false);

  /**
   * Check Ollama service health
   */
  const checkHealth = useCallback(async () => {
    setIsChecking(true);
    debugLog("info", "llm", "Checking Ollama health", { module: "useModelHealth" });

    try {
      const isHealthy = await core.invoke<boolean>("ollama_health_check", {});

      const newStatus: OllamaHealthStatus = {
        isHealthy,
        lastChecked: Date.now(),
      };
      if (!isHealthy) {
        newStatus.error = "Ollama service is not responding";
      }
      setHealthStatus(newStatus);

      debugLog(isHealthy ? "info" : "warn", "llm", `Health check result: ${String(isHealthy)}`, {
        module: "useModelHealth",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Health check failed";
      const errorStatus: OllamaHealthStatus = {
        error: errorMessage,
        isHealthy: false,
        lastChecked: Date.now(),
      };
      setHealthStatus(errorStatus);

      debugLog("error", "llm", `Health check failed: ${errorMessage}`, {
        module: "useModelHealth",
      });
    } finally {
      setIsChecking(false);
    }
  }, []);

  /**
   * Get available models from Ollama
   */
  const fetchModels = useCallback(async () => {
    if (!healthStatus.isHealthy) {
      return;
    }

    debugLog("info", "llm", "Fetching available models", { module: "useModelHealth" });

    try {
      const models = await core.invoke<string[]>("ollama_get_models", {});
      setAvailableModels(models);

      debugLog("info", "llm", `Found ${String(models.length)} models`, {
        module: "useModelHealth",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch models";
      debugLog("error", "llm", `Failed to fetch models: ${errorMessage}`, {
        module: "useModelHealth",
      });
    }
  }, [healthStatus.isHealthy]);

  /**
   * Get default model
   */
  const getDefaultModel = useCallback(async (): Promise<string> => {
    try {
      const defaultModel = await core.invoke<string>("ollama_get_default_model", {});
      debugLog("info", "llm", `Default model: ${defaultModel}`, { module: "useModelHealth" });
      return defaultModel;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to get default model";
      debugLog("error", "llm", `Failed to get default model: ${errorMessage}`, {
        module: "useModelHealth",
      });
      return "llama3.2:3b";
    }
  }, []);

  /**
   * Auto-check health on mount
   */
  useEffect(() => {
    void checkHealth();
  }, [checkHealth]);

  /**
   * Fetch models when health is restored
   */
  useEffect(() => {
    if (healthStatus.isHealthy && availableModels.length === 0) {
      void fetchModels();
    }
  }, [healthStatus.isHealthy, availableModels.length, fetchModels]);

  return {
    availableModels,
    checkHealth,
    fetchModels,
    getDefaultModel,
    healthStatus,
    isChecking,
  };
};
